import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { activateAccount } from '../api/auth'

type Status = 'loading' | 'success' | 'error'

export default function ActivateAccountPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<Status>('loading')
  const [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    const token = searchParams.get('token')
    if (!token) {
      setStatus('error')
      setErrorMessage('No activation token found in the URL.')
      return
    }

    activateAccount(token)
      .then(() => {
        setStatus('success')
        // Redirect to login with success banner after a short delay
        setTimeout(() => {
          navigate('/login?activated=true', { replace: true })
        }, 1500)
      })
      .catch((err) => {
        setStatus('error')
        const detail = err?.response?.data?.detail
        if (detail === 'link_expired') {
          setErrorMessage('Your activation link has expired. Please sign up again or contact support.')
        } else if (detail === 'user_not_found') {
          setErrorMessage('No account was found for this activation link.')
        } else {
          setErrorMessage('Account activation failed. The link may already have been used.')
        }
      })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo */}
        <a href="/" className="flex justify-center items-center space-x-2 mb-8">
          <img src="/static/images/favicon.ico" alt="ReadLocker" className="w-10 h-10" />
          <span className="text-2xl font-bold text-gray-900">ReadLocker</span>
        </a>

        <div className="bg-white py-8 px-4 shadow-xl shadow-gray-200/50 sm:rounded-2xl sm:px-10 text-center">
          {status === 'loading' && (
            <>
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Activating your account…</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Account activated!</h2>
              <p className="text-gray-500 text-sm">Redirecting you to sign in…</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Activation failed</h2>
              <p className="text-sm text-gray-500 mb-6" data-testid="activation-error">{errorMessage}</p>
              <a
                href="/login"
                className="inline-block w-full py-3 px-4 rounded-xl text-sm font-medium text-white bg-primary hover:bg-secondary transition-colors text-center"
              >
                Go to Sign in
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

